// src/pages/Home.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../index.css";

const focusItems = {
  entreprise: {
    kicker: "Entreprise",
    title: (
      <>
        <span>Vos images sont souvent</span>
        <span>la première impression</span>
        <span>que vous donnez</span>
      </>
    ),
    text: "Des visuels professionnels renforcent votre crédibilité, modernisent votre communication et mettent en valeur vos équipes, vos espaces et votre savoir-faire. L’objectif n’est pas seulement de produire de belles images, mais de créer des visuels concrets, cohérents et directement exploitables.",
    bullets: [
      "Portraits professionnels",
      "Photos d’équipe en situation",
      "Valorisation des locaux et espaces",
      "Reportages métier & événements",
    ],
    image: "/images/artisan.jpg",
    alt: "Photographie pour entreprise",
    cta: "DÉCOUVRIR L’OFFRE ENTREPRISE",
  },

  portrait: {
    kicker: "Portrait",
    title: (
      <>
        <span>Des images naturelles</span>
        <span>et élégantes pour</span>
        <span>vous mettre en valeur</span>
      </>
    ),
    text: "Que ce soit pour un portrait personnel, professionnel ou artistique, l’objectif est de créer des images sincères, soignées et fidèles à votre personnalité. Une séance pensée pour vous mettre à l’aise et obtenir des photos fortes et naturelles.",
    bullets: [
      "Portraits naturels et soignés",
      "Séances en extérieur ou en studio",
      "Accompagnement simple et fluide",
      "Images adaptées à un usage personnel ou professionnel",
    ],
    image: "/images/services/portrait.jpg",
    alt: "Photographie portrait",
    cta: "DÉCOUVRIR L’OFFRE PORTRAIT",
  },

  evenement: {
    kicker: "Événement",
    title: (
      <>
        <span>Des images vivantes</span>
        <span>pour raconter vos</span>
        <span>moments importants</span>
      </>
    ),
    text: "Anniversaire, baptême, soirée privée ou événement professionnel : je réalise des images spontanées, naturelles et cohérentes pour retranscrire l’ambiance, les échanges et les instants forts de votre événement.",
    bullets: [
      "Reportage discret et réactif",
      "Moments clés et ambiance générale",
      "Images naturelles et authentiques",
      "Livraison rapide et galerie sécurisée",
    ],
    image: "/images/services/evenement.jpg",
    alt: "Photographie événement",
    cta: "DÉCOUVRIR L’OFFRE ÉVÉNEMENT",
  },

  mariage: {
    kicker: "Mariage",
    title: (
      <>
        <span>Un reportage sensible</span>
        <span>et sincère pour raconter</span>
        <span>votre journée</span>
      </>
    ),
    text: "Le mariage mérite des images justes, lumineuses et naturelles. Mon approche vise à raconter votre journée avec émotion, discrétion et cohérence, afin de vous laisser des souvenirs forts et intemporels.",
    bullets: [
      "Reportage naturel et discret",
      "Moments forts et détails importants",
      "Approche humaine et sensible",
      "Images livrées pour le web et l’impression",
    ],
    image: "/images/services/mariage.jpg",
    alt: "Photographie mariage",
    cta: "DÉCOUVRIR L’OFFRE MARIAGE",
  },
};

export default function Home() {
  const [activeTab, setActiveTab] = useState("entreprise");

  const heroStyle = {
    backgroundImage: "url('/images/hero.jpg')",
    backgroundPosition: "center",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
  };

  const current = focusItems[activeTab];

  return (
    <main className="home-page">
      {/* HERO */}
      <section className="home-hero" style={heroStyle}>
        <div className="home-hero-overlay" />

        <div className="home-hero-content">
          <p className="home-kicker">HLBK Photo</p>

          <h1 className="home-title">
            <span>Des photos professionnelles</span>
            <span>pour renforcer votre image</span>
            <span>et attirer plus de clients</span>
          </h1>

          <p className="home-subtitle">
            Portraits, équipes, reportages : des images professionnelles pensées
            <br />
            pour valoriser votre activité et inspirer confiance.
          </p>

          <div className="home-hero-points">
            <span>Basé à Tomblaine</span>
            <span>Nancy & alentours</span>
            <span>Livraison rapide</span>
          </div>

          <div className="home-cta-group">
            <Link to="/services" className="btn-cta btn-cta-red">
              OBTENIR UN DEVIS RAPIDE
            </Link>
            <Link to="/galerie" className="btn-cta btn-cta-outline">
              VOIR LA GALERIE
            </Link>
          </div>
        </div>
      </section>

      {/* OFFER */}
      <section className="home-section home-offer">
        <div className="home-container">
          <div className="home-offer-box">
            <p className="section-kicker">Entreprise — Format découverte</p>
            <h2 className="section-title">
              Une première session simple <br />
              pour moderniser votre image
            </h2>
            <p className="section-text">
              Un format pensé pour les entreprises qui souhaitent mettre à jour
              rapidement
              <br />
              leurs portraits, leur équipe ou leurs premiers visuels de communication.
            </p>

            <ul className="home-offer-list">
              <li>Session entreprise découverte — à partir de 290€ HT</li>
              <li>
                Format idéal pour initier une collaboration et créer vos premiers
                visuels professionnels
              </li>
              <li>
                Environ 1h de prise de vue sur site (portraits / équipe / environnement)
              </li>
              <li>Déplacement inclus jusqu’à 30 km autour de Tomblaine</li>
              <li>Livraison rapide des images optimisées pour web & réseaux</li>
            </ul>

            <p className="home-offer-note">
              Disponibilités limitées selon les périodes.
            </p>

            <Link to="/services" className="btn-cta btn-cta-red">
              DÉCOUVRIR LES PRESTATIONS
            </Link>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="home-section home-impact">
        <div className="home-container">
          <h2 className="section-title">
            Pourquoi investir dans des photos professionnelles ?
          </h2>
          <p className="section-text">
            Aujourd’hui, vos visuels sont souvent la première impression que
            vous donnez. Des images professionnelles permettent de renforcer
            votre crédibilité, moderniser votre communication et valoriser votre
            activité de façon claire et cohérente.
          </p>

          <div className="home-impact-grid">
            <div className="home-impact-card">
              <h3>Renforcer votre image</h3>
              <p>
                Des visuels soignés rassurent et donnent une image plus
                professionnelle de votre activité.
              </p>
            </div>

            <div className="home-impact-card">
              <h3>Attirer plus de clients</h3>
              <p>
                De bonnes images donnent envie de découvrir votre entreprise,
                vos services et votre univers.
              </p>
            </div>

            <div className="home-impact-card">
              <h3>Moderniser votre communication</h3>
              <p>
                Site web, réseaux sociaux, LinkedIn, supports commerciaux :
                vos images deviennent enfin cohérentes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="home-section home-intro">
        <div className="home-container">
          <h2 className="section-title">
            Une approche simple, humaine et soignée
          </h2>
          <p className="section-text">
            Chaque projet mérite des images cohérentes, sensibles et utiles.
            <br />
            Que ce soit pour valoriser une entreprise, raconter un mariage,
            réaliser un portrait ou couvrir un événement, mon objectif est de
            créer des visuels qui vous ressemblent vraiment et qui trouvent leur
            place dans votre communication ou votre histoire.
          </p>
        </div>
      </section>

      {/* FOCUS TABS */}
      <section className="home-section home-focus-tabs-section">
        <div className="home-container">
          <div className="home-tabs-nav">
            <button
              className={`home-tab-btn ${activeTab === "entreprise" ? "active" : ""}`}
              onClick={() => setActiveTab("entreprise")}
              type="button"
            >
              Entreprise
            </button>
            <button
              className={`home-tab-btn ${activeTab === "portrait" ? "active" : ""}`}
              onClick={() => setActiveTab("portrait")}
              type="button"
            >
              Portrait
            </button>
            <button
              className={`home-tab-btn ${activeTab === "evenement" ? "active" : ""}`}
              onClick={() => setActiveTab("evenement")}
              type="button"
            >
              Événement
            </button>
            <button
              className={`home-tab-btn ${activeTab === "mariage" ? "active" : ""}`}
              onClick={() => setActiveTab("mariage")}
              type="button"
            >
              Mariage
            </button>
          </div>

          <div className="home-focus-grid">
            <div className="home-focus-text">
              <p className="section-kicker">{current.kicker}</p>
              <h2 className="section-title">{current.title}</h2>
              <p className="section-text">{current.text}</p>

              <ul className="home-benefits">
                {current.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              <Link to="/services" className="btn-cta btn-cta-red">
                {current.cta}
              </Link>
            </div>

            <div className="home-focus-image">
              <img src={current.image} alt={current.alt} />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="home-section">
        <div className="home-container">
          <h2 className="section-title">Services</h2>

          <div className="home-services-grid">
            <article className="home-service-card">
              <img src="/images/services/entreprise.jpg" alt="Entreprise" />
              <h3>Entreprise</h3>
              <p>
                Portraits professionnels, reportages, mise en valeur des
                équipes, des espaces et de votre communication.
              </p>
              <Link to="/services" className="service-link">
                En savoir plus
              </Link>
            </article>

            <article className="home-service-card">
              <img src="/images/services/mariage.jpg" alt="Mariage" />
              <h3>Mariage</h3>
              <p>
                Un reportage naturel, sincère et soigné pour raconter votre
                journée avec justesse.
              </p>
              <Link to="/services" className="service-link">
                En savoir plus
              </Link>
            </article>

            <article className="home-service-card">
              <img src="/images/services/portrait.jpg" alt="Portrait" />
              <h3>Portrait</h3>
              <p>
                Des images naturelles et élégantes, en extérieur ou en studio,
                pour vous mettre en valeur avec simplicité.
              </p>
              <Link to="/services" className="service-link">
                En savoir plus
              </Link>
            </article>

            <article className="home-service-card">
              <img src="/images/services/evenement.jpg" alt="Événement" />
              <h3>Événement</h3>
              <p>
                Des images vivantes et authentiques pour garder une trace forte
                de vos moments importants.
              </p>
              <Link to="/services" className="service-link">
                En savoir plus
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* WHY ME */}
      <section className="home-section home-why">
        <div className="home-container">
          <h2 className="section-title">Pourquoi travailler avec moi ?</h2>

          <div className="home-why-grid">
            <div className="home-why-card">
              <h3>Approche naturelle</h3>
              <p>
                Des images vivantes, sincères et cohérentes avec votre univers.
              </p>
            </div>

            <div className="home-why-card">
              <h3>Accompagnement simple</h3>
              <p>
                Un échange clair, une organisation fluide et une prestation
                pensée selon vos besoins.
              </p>
            </div>

            <div className="home-why-card">
              <h3>Images utiles</h3>
              <p>
                Des visuels conçus pour être beaux, mais aussi réellement utiles
                à votre communication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="home-section home-final-cta">
        <div className="home-container home-final-box">
          <h2 className="section-title">Un projet ? Parlons-en.</h2>
          <p className="section-text">
            Je vous propose un échange simple pour comprendre votre besoin et
            vous orienter vers la solution la plus adaptée.
          </p>
          <Link to="/services" className="btn-cta btn-cta-red">
            OBTENIR UN DEVIS RAPIDE
          </Link>
        </div>
      </section>
    </main>
  );
}